// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://bit.ly/CRA-PWA

const runInBrowser = typeof window !== 'undefined' && !(window as any).ssr;

const isLocalhost = runInBrowser
  ? Boolean(
      window.location.hostname === 'localhost' ||
        // [::1] is the IPv6 localhost address.
        window.location.hostname === '[::1]' ||
        // 127.0.0.1/8 is considered localhost for IPv4.
        window.location.hostname.match(
          /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
        ),
    )
  : undefined;

interface Config {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
}

export function register(config?: Config) {
  // console.log('process.env.REGISTER_SW',process.env)
  if (
    (process.env.NODE_ENV === 'production' ||
      process.env.REACT_APP_REGISTER_SW === 'true') &&
    'serviceWorker' in navigator
  ) {
    console.log('serviceWorker.ts register', config);
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(
      (process as { env: { [key: string]: string } }).env.PUBLIC_URL,
      window.location.href,
    );
    console.log('publicUrl', publicUrl);

    if (publicUrl.origin !== window.location.origin) {
      console.log(
        `Skip serviceWorker registration: wrong location ${publicUrl.origin} !== ${window.location.origin}`,
      );
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return;
    }

    window.addEventListener('load', () => {
      // const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;
      const swUrl = `${process.env.PUBLIC_URL}/sw.js`;
      console.log('SW serviceWorker.ts swUrl', swUrl);
      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service ' +
              'worker. To learn more, visit https://bit.ly/CRA-PWA',
          );
        });
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  // console.log('registerValidSW', swUrl);
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      console.log('registerValidSW.registration', registration);
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        // console.log('registerValidSW.registration.onupdatefound installingWorker', installingWorker);
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          console.log('installingWorker.onstatechange', installingWorker.state);
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              console.log(
                'New content is available and will be used when all ' +
                  'tabs for this page are closed. See https://bit.ly/CRA-PWA.',
              );

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              console.log('Content is cached for offline use.');

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  console.log('checkValidServiceWorker', swUrl);
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl)
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        console.log(
          'No service worker found. Probably a different app. Reload the page.',
        );
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        console.log('Service worker found. Proceed as normal.');
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log(
        'No internet connection found. App is running in offline mode.',
      );
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.unregister();
    });
  }
}

// Init comunication with SW
const messageChannel = runInBrowser && new MessageChannel();

// First we initialize the channel by sending
// the port to the Service Worker (this also
// transfers the ownership of the port)
if (messageChannel) {
  navigator.serviceWorker?.controller?.postMessage(
    {
      type: 'INIT_PORT',
    },
    [messageChannel.port2],
  );
}

// get curre FirebaseToken using Service Worker channel
export const getFirebaseTokenFromSW = () =>
  new Promise((resolve, reject) => {
    if (!messageChannel) {
      reject('messageChannel not defined');
      return;
    }
    // Listen to the response
    messageChannel.port1.onmessage = (event) => {
      // console.log('----------RESOLVED getFirebaseTokenFromSW --------')
      messageChannel.port1.onmessage = null;
      resolve(event?.data?.token);
    };

    // Then we send our first message
    navigator.serviceWorker?.controller?.postMessage({
      type: 'GET_FIREBASE_TOKEN',
    });
  });

export const getFcmTokenFromSW = () =>
  new Promise<string | null>((resolve, reject) => {
    if (!messageChannel) {
      reject('messageChannel not defined');
      return;
    }
    console.log('getFcmTokenFromSW called');
    messageChannel.port1.onmessage = (event) => {
      messageChannel.port1.onmessage = null;
      resolve(event?.data?.token ?? null);
    };
    navigator.serviceWorker?.controller?.postMessage({
      type: 'GET_FCM_TOKEN',
    });
  });
